import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Snackbar,
  Alert,
  Autocomplete,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { saidaService, SaidaDetalhes } from '../../services/saida';
import { vendedorService, Vendedor } from '../../services/vendedor';
import DatePickerField from '../common/DatePickerField';

const SaidaAlterarDatas: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const idSaida = Number(id);

  const [notaSaida, setNotaSaida] = useState<SaidaDetalhes | null>(null);
  const [novaDataSaida, setNovaDataSaida] = useState('');
  const [novaDataRetorno, setNovaDataRetorno] = useState('');
  const [vendedores, setVendedores] = useState<Vendedor[]>([]);
  const [selectedVendedor, setSelectedVendedor] = useState<Vendedor | null>(null);
  const [bloqueado, setBloqueado] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' as 'error' | 'success' });

  const { data: saidaData, isLoading, error } = useQuery<SaidaDetalhes>({
    queryKey: ['saida', 'resumido', idSaida],
    queryFn: () => saidaService.getByIdResumido(idSaida),
    enabled: !!idSaida,
  });

  useEffect(() => {
    if (saidaData) {
      setNotaSaida(saidaData);
    }
  }, [saidaData]);

  useEffect(() => {
    if (notaSaida?.idPraca) {
      vendedorService.getVendedoresByPraca(notaSaida.idPraca).then((data) => {
        setVendedores(data);
      }).catch((err) => {
        console.error('Erro ao carregar vendedores', err);
      });
    }
  }, [notaSaida]);

  const convertToApiDate = (dateString: string): string => {
    if (!dateString) return '';
    const [day, month, year] = dateString.split('/');
    return `${year}-${month}-${day}T00:00:00`;
  };

  const handleVendedorChange = (vendedor: Vendedor | null) => {
    setSelectedVendedor(vendedor);
    if (!vendedor) {
      setBloqueado(false);
      return;
    }

    const situacao = Number(vendedor.situacao);
    if (situacao !== 1) {
      setBloqueado(true);
    } else {
      setBloqueado(false);
    }

    if (situacao === 2) {
      setSnackbar({
        open: true,
        message: 'Vendedor bloqueado por falta de pagamento, consulte o valor da dívida no cadastro ou entre em contato com a empresa.',
        severity: 'error',
      });
    } else if (situacao === 3) {
      setSnackbar({
        open: true,
        message: 'Vendedor bloqueado por falta de devolução de mercadoria, entre em contato com a empresa.',
        severity: 'error',
      });
    } else if (situacao === 4) {
      setSnackbar({
        open: true,
        message: 'Vendedor bloqueado por CPF negativado no SPC, entre em contato com a empresa.',
        severity: 'error',
      });
    }
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!notaSaida) throw new Error('Nota de saída não carregada.');
      if (bloqueado) throw new Error('Não é possível salvar com vendedor bloqueado.');

      const data = {
        idSaida,
        dataSaidaAnterior: notaSaida.dataSaida || '',
        dataRetornoAnterior: notaSaida.dataRetorno || notaSaida.dataPrevistaRetorno || '',
        idVendedorAnterior: notaSaida.idVendedor || 0,
        novaDataNotaSaida: convertToApiDate(novaDataSaida),
        novaDataNotaRetorno: convertToApiDate(novaDataRetorno),
        idVendedor: selectedVendedor?.id || notaSaida.idVendedor || 0,
      };
      return saidaService.salvarAlteracaoDeDatas(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saidas'] });
      queryClient.invalidateQueries({ queryKey: ['saida', idSaida] });
      queryClient.invalidateQueries({ queryKey: ['saida', 'resumido', idSaida] });
      setSnackbar({ open: true, message: 'Datas alteradas com sucesso.', severity: 'success' });
      navigate('/saidas');
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: 'Erro ao alterar datas: ' + (error.message || error),
        severity: 'error',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!novaDataSaida && !novaDataRetorno && !selectedVendedor) {
      setSnackbar({ open: true, message: 'Informe pelo menos uma nova data ou um novo vendedor.', severity: 'error' });
      return;
    }
    saveMutation.mutate();
  };

  const handleBack = () => {
    navigate('/saidas');
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  if (error || !notaSaida) {
    const errorMessage = (error as any)?.response?.data?.title
      || (error as any)?.response?.data?.message
      || (error as any)?.message
      || 'Erro ao carregar a nota de saída.';
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, flexDirection: 'column', alignItems: 'center' }}>
        <Typography color="error" gutterBottom>Erro ao carregar a nota de saída.</Typography>
        <Typography color="error" variant="body2">{errorMessage}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Alterar Datas - Saída #{idSaida}
        </Typography>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Voltar
        </Button>
      </Box>

      <form onSubmit={handleSubmit}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Promotor de vendas"
                value={notaSaida.nomePromotor || ''}
                slotProps={{ input: { readOnly: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Praça"
                value={notaSaida.praca || ''}
                slotProps={{ input: { readOnly: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Vendedor(a) atual"
                value={notaSaida.nomeVendedor || notaSaida.vendedor || ''}
                slotProps={{ input: { readOnly: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="CPF"
                value={notaSaida.cpf || ''}
                slotProps={{ input: { readOnly: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Data de saída atual"
                value={notaSaida.dataSaidaString || ''}
                slotProps={{ input: { readOnly: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Data de retorno atual"
                value={notaSaida.dataRetornoString || notaSaida.dataPrevistaRetornoString || ''}
                slotProps={{ input: { readOnly: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <DatePickerField
                label="Nova data saída"
                value={novaDataSaida}
                onChange={setNovaDataSaida}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <DatePickerField
                label="Nova data retorno"
                value={novaDataRetorno}
                onChange={setNovaDataRetorno}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                options={vendedores}
                getOptionLabel={(v) => `${v.nome} - ${v.cpf || ''}`}
                value={selectedVendedor}
                onChange={(_, value) => handleVendedorChange(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Novo vendedor(a)"
                    placeholder="Selecione um vendedor"
                  />
                )}
              />
            </Grid>
          </Grid>
        </Paper>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={handleBack} disabled={saveMutation.isPending}>
            Voltar
          </Button>
          <Button
            variant="contained"
            type="submit"
            startIcon={<SaveIcon />}
            disabled={saveMutation.isPending || bloqueado}
          >
            {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
          </Button>
        </Box>
      </form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SaidaAlterarDatas;
